import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Search, ShoppingCart, ClipboardList, Heart, Bell, Wallet, MapPin, Settings, Truck, CheckCircle2, Clock, XCircle, Phone, Mail } from "lucide-react";
import client from "@/lib/api";
import useMeta from "@/lib/useMeta";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import ProductCard from "@/components/ProductCard";

const STATUS_META = {
  processing: ["قيد التجهيز", Clock, "var(--k-gold-deep)"],
  shipped: ["قيد الشحن", Truck, "var(--k-gold-deep)"],
  delivered: ["تم التسليم", CheckCircle2, "var(--k-emerald)"],
  cancelled: ["ملغاة", XCircle, "var(--k-ruby)"],
};
const PAY_LABEL = { paid: "مدفوع", pending: "بانتظار الدفع", cod_pending: "عند الاستلام", awaiting_transfer: "بانتظار التحويل", refunded: "مُسترد" };

export default function BuyerDashboard() {
  const nav = useNavigate();
  const meta = useMeta();
  const { user, ready } = useAuth();
  const [ov, setOv] = useState(null);
  const [offers, setOffers] = useState([]);
  const [active, setActive] = useState("overview");

  useEffect(() => {
    if (!ready) return;
    if (user === false) { nav("/auth?next=/dashboard"); return; }
    client.get("/buyer/overview").then((r) => setOv(r.data)).catch(() => {});
    client.get("/products").then((r) => setOffers(r.data.slice(0, 8))).catch(() => {});
  }, [ready, user]);

  const navItems = [
    { key: "overview", label: "لوحة التحكم", icon: LayoutDashboard },
    { key: "browse", label: "تصفح المنتجات", icon: Search },
    { key: "cart", label: "سلة المشتريات", icon: ShoppingCart },
    { key: "orders", label: "طلباتي", icon: ClipboardList },
    { key: "favorites", label: "المفضلة", icon: Heart },
    { key: "notifications", label: "الإشعارات", icon: Bell },
    { key: "financial", label: "البيانات المالية", icon: Wallet },
    { key: "addresses", label: "العناوين", icon: MapPin },
    { key: "settings", label: "إعدادات الحساب", icon: Settings },
  ];
  // sections rendered inside the dashboard main panel:
  const SECTIONS = ["overview", "financial"];
  const ROUTES = { browse: "/browse", cart: "/cart", orders: "/orders", favorites: "/browse", notifications: "/notifications", addresses: "/account", settings: "/account" };
  const go = (k) => {
    if (SECTIONS.includes(k)) { setActive(k); window.scrollTo({ top: 0, behavior: "smooth" }); }
    else nav(ROUTES[k] || "/");
  };

  const countFor = (s) => (ov?.statusCounts.find((x) => x.status === s)?.count) || 0;

  return (
    <DashboardShell roleLabel="لوحة المشتري" topLabel="لوحة المشتري" items={navItems} active={active} onSelect={go}>
      {active === "overview" && (
        <div className="p-4" dir="rtl">
          <h1 className="font-head text-[20px] font-black">مرحباً بك، {user?.name || "عميلنا"}</h1>
          <p className="text-[12.5px] mb-3" style={{ color: "var(--k-ink-soft)" }}>تصفّح أحدث العروض وتابع حالة طلباتك</p>

          {/* Category quick filters */}
          <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-1 mb-3">
            {meta.categories.slice(0, 8).map((c) => (
              <button key={c.id} data-testid={`qf-${c.id}`} onClick={() => nav(`/browse?cat=${c.id}`)} className="k-tap shrink-0 flex flex-col items-center gap-1.5 px-4 py-3 rounded-2xl" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)", minWidth: 92 }}>
                <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "var(--k-gold-tint)", color: "var(--k-gold-deep)" }}><Search size={18} /></span>
                <span className="text-[11.5px] font-semibold">{c.name}</span>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
            <div className="xl:col-span-2 space-y-3">
              <div className="rounded-2xl p-6 relative overflow-hidden" style={{ background: "var(--k-ink)" }}>
                <h2 className="font-head text-[22px] font-black text-white">منتجات بالجملة بأسعار مميزة</h2>
                <p className="text-[12.5px] mt-1 mb-3" style={{ color: "#C7B08A" }}>تجار موثوقون · جودة عالية · توصيل سريع</p>
                <button onClick={() => nav("/browse")} className="k-tap px-5 py-2.5 rounded-xl font-bold text-[13px]" style={{ background: "var(--k-gold)", color: "var(--k-ink)" }}>تصفح الآن</button>
              </div>
              <div className="rounded-2xl p-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
                <div className="flex justify-between items-center mb-3">
                  <h2 className="font-head text-[15px] font-bold">أحدث العروض</h2>
                  <button onClick={() => nav("/browse")} className="k-tap text-[11.5px] font-bold bg-transparent border-0" style={{ color: "var(--k-gold-deep)" }}>عرض الكل ›</button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                  {offers.map((p) => <ProductCard key={p.id} p={p} />)}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="rounded-2xl p-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
                <div className="flex justify-between items-center mb-3">
                  <h2 className="font-head text-[15px] font-bold">حالة طلباتي</h2>
                  <button onClick={() => nav("/orders")} className="k-tap text-[11.5px] font-bold bg-transparent border-0" style={{ color: "var(--k-gold-deep)" }}>عرض الكل ›</button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(STATUS_META).map(([s, [label, Icon, col]]) => (
                    <div key={s} data-testid={`bstat-${s}`} className="rounded-xl p-3 text-center" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }}>
                      <Icon size={18} className="mx-auto mb-1" style={{ color: col }} />
                      <div className="font-num text-[18px] font-black">{countFor(s)}</div>
                      <div className="text-[10.5px]" style={{ color: "var(--k-ink-soft)" }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl p-4 text-center" style={{ background: "var(--k-ink)", color: "#fff" }}>
                <div className="text-[11px] text-white/70">إجمالي الفواتير</div>
                <div className="font-num text-[26px] font-black my-1">{ov?.invoicesTotal ?? 0} <span className="text-[13px]">د.ل</span></div>
                <button onClick={() => go("financial")} className="k-tap w-full py-2 rounded-lg text-[12px] font-bold" style={{ background: "var(--k-gold)", color: "var(--k-ink)" }}>البيانات المالية</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {active === "financial" && (
        <div className="p-4" dir="rtl">
          <h1 className="font-head text-[20px] font-black">البيانات المالية</h1>
          <p className="text-[12.5px] mb-4" style={{ color: "var(--k-ink-soft)" }}>رصيد محفظتك وفواتيرك ومعاملاتك</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 mb-4">
            <div className="rounded-2xl p-4 text-center" style={{ background: "var(--k-ink)", color: "#fff" }}>
              <Wallet size={20} className="mx-auto mb-1" style={{ color: "var(--k-gold)" }} />
              <div className="text-[11px] text-white/70">رصيد المحفظة</div>
              <div className="font-num text-[24px] font-black my-1" data-testid="wallet-balance">{ov?.walletBalance ?? 0} <span className="text-[12px]">د.ل</span></div>
            </div>
            <div className="rounded-2xl p-4 text-center" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
              <div className="text-[11px]" style={{ color: "var(--k-ink-soft)" }}>إجمالي الفواتير</div>
              <div className="font-num text-[24px] font-black my-1">{ov?.invoicesTotal ?? 0} <span className="text-[12px]">د.ل</span></div>
            </div>
            <div className="rounded-2xl p-4 text-center" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
              <div className="text-[11px]" style={{ color: "var(--k-ink-soft)" }}>عدد الطلبات</div>
              <div className="font-num text-[24px] font-black my-1">{ov?.ordersCount ?? 0}</div>
            </div>
          </div>

          <div className="rounded-2xl p-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
            <div className="flex justify-between items-center mb-3">
              <h2 className="font-head text-[15px] font-bold">معاملاتي الأخيرة</h2>
              <button onClick={() => nav("/orders")} className="k-tap text-[11.5px] font-bold bg-transparent border-0" style={{ color: "var(--k-gold-deep)" }}>كل الطلبات ›</button>
            </div>
            {!ov || ov.recentOrders.length === 0 ? (
              <p className="text-[12.5px]" style={{ color: "var(--k-ink-soft)" }}>لا توجد معاملات بعد.</p>
            ) : (
              <div className="overflow-x-auto no-scrollbar">
                <table className="w-full text-right" style={{ minWidth: 480 }}>
                  <thead><tr className="text-[10.5px]" style={{ color: "var(--k-ink-soft)" }}>
                    <th className="pb-2 font-semibold">رقم الطلب</th><th className="pb-2 font-semibold">المبلغ</th><th className="pb-2 font-semibold">حالة الدفع</th><th className="pb-2 font-semibold">التاريخ</th>
                  </tr></thead>
                  <tbody>
                    {ov.recentOrders.map((o) => (
                      <tr key={o.orderId} data-testid={`fin-${o.orderId}`} className="text-[11.5px]" style={{ borderTop: "1px solid var(--k-line)" }}>
                        <td className="py-2 font-num font-bold">{o.orderId}</td>
                        <td className="py-2 font-num font-bold">{o.total} د.ل</td>
                        <td className="py-2"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: o.payStatus === "paid" ? "var(--k-emerald-tint)" : "var(--k-gold-tint)", color: o.payStatus === "paid" ? "var(--k-emerald)" : "var(--k-gold-deep)" }}>{PAY_LABEL[o.payStatus] || o.payStatus}</span></td>
                        <td className="py-2 font-num text-[10px]" style={{ color: "var(--k-ink-soft)" }}>{(o.created_at || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Contact us */}
          <div className="rounded-2xl p-4 mt-3" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
            <h2 className="font-head text-[15px] font-bold mb-2">اتصل بنا</h2>
            <div className="flex flex-col gap-2 text-[13px]">
              <a href="tel:0913259296" data-testid="contact-phone" className="flex items-center justify-between gap-2 pb-2 mb-1" style={{ color: "var(--k-ink)", borderBottom: "1px solid var(--k-line)" }}><span className="flex items-center gap-2 font-num"><Phone size={15} style={{ color: "var(--k-gold-deep)" }} /> 0913259296</span><span className="text-[11px]" style={{ color: "var(--k-ink-soft)" }}>رقم موحّد لكل الأقسام</span></a>
              <a href="mailto:info@kazuoni.com" data-testid="contact-email-info" className="flex items-center gap-2" style={{ color: "var(--k-ink)" }}><Mail size={15} style={{ color: "var(--k-gold-deep)" }} /> info@kazuoni.com</a>
              <a href="mailto:sales@kazuoni.com" data-testid="contact-email-sales" className="flex items-center gap-2" style={{ color: "var(--k-ink)" }}><Mail size={15} style={{ color: "var(--k-gold-deep)" }} /> sales@kazuoni.com</a>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
